import { useState, useEffect, useRef } from "react";
import { useFormContext, Controller } from "react-hook-form";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import _ from "lodash";

const MultiSelectDateTimePicker = ({
    label,
    name,
    required,
    placeholder = "Select Dates",
    className = "",
    dateFormat,
    shouldUnregister,
    excludeTimes,
    filterTime,
    maxDate,
    minDate,
    onChangeHandler,
    readOnly,
    padding = "px-4 py-2",
}) => {
    const {
        formState: { errors },
        control,
        watch
    } = useFormContext();

    const [isOpen, setIsOpen] = useState(false);
    const [inputRef, setInputRef] = useState(null);
    const [selectedDates, setSelectedDates] = useState([]);
    const [selectedTime, setSelectedTime] = useState(new Date());

    const mergeDateWithTime = (date, time) => {
        const newDate = new Date(date);
        if (!(time instanceof Date) || isNaN(time)) return newDate;
        newDate.setHours(time.getHours(), time.getMinutes(), 0, 0);
        return newDate;
    };


    const handleDateToggle = (date) => {
        if (!date || readOnly) return;

        const dateWithoutTime = new Date(date);
        dateWithoutTime.setHours(0, 0, 0, 0);

        let updated = [...selectedDates];
        const exists = updated.find((d) => d.toDateString() === dateWithoutTime.toDateString());

        if (exists) {
            updated = updated.filter((d) => d.toDateString() !== dateWithoutTime.toDateString());
        } else {
            updated.push(dateWithoutTime);
        }

        setSelectedDates(updated);
    };

    const handleTimeChange = (time, onChange) => {
        if (!time) return;
        setSelectedTime(time);

        const merged = selectedDates.map((d) => mergeDateWithTime(d, time));
        onChange(merged);
        if (onChangeHandler) onChangeHandler(merged);
    };


    const handleDone = (onChange) => {
        const merged = selectedDates.map((d) => mergeDateWithTime(d, selectedTime));
        onChange(merged);
        if (onChangeHandler) onChangeHandler(merged);
        setIsOpen(false);
    };
    const watchedValue = watch(name);

    useEffect(() => {
        if (!Array.isArray(watchedValue) || watchedValue.length === 0) return;
        const validDates = watchedValue
            .map((d) => {
                const dt = new Date(d);
                return isNaN(dt.getTime()) ? null : dt;
            })
            .filter(Boolean);

        if (validDates.length === 0) return;

        const uniqueDates = Array.from(
            new Set(validDates.map((d) => d.toDateString()))
        ).map((ds) => new Date(ds));

        setSelectedDates(uniqueDates);
        const sample = validDates[0];
        if (sample) setSelectedTime(sample);
    }, [watchedValue]);


    return (
        <div className={`${padding} ${className}`}>
            {label && (
                <label>
                    <span className='text-scogo99 font-normal text-font09 md:text-font11 labelFotnSize'>
                        {label} {required && <span className='text-scogoorange'>*</span>}
                    </span>
                </label>
            )}
            <div className="relative">
                <Controller
                    key={name}
                    control={control}
                    name={name}
                    rules={{ required: { value: required, message: "Required" } }}
                    shouldUnregister={shouldUnregister}
                    render={({ field: { onChange, value } }) => {
                        const maxVisibleDates = 2;
                        const visibleDates = (value || []).slice(0, maxVisibleDates);
                        const remainingCount = (value?.length || 0) - maxVisibleDates;
                        return (
                            <>
                                <div
                                    ref={setInputRef}
                                    className={`border ${readOnly ? 'border-scogogray bg-scogof2' : 'border-scogoccc focus:border-scogoprimary bg-white'} w-full pl-10 rounded-6px text-scogo99 font-normal text-font09 md:text-font11 py-2`}
                                    onClick={() => !readOnly && setIsOpen(true)}
                                    title={(value || []).map((d) => new Date(d).toLocaleString()).join(", ")}
                                >
                                    {value && value.length > 0
                                        ? visibleDates.map((d) => new Date(d).toLocaleDateString()).join(", ") +
                                        (remainingCount > 0 ? ` +${remainingCount} more` : "")
                                        : placeholder}
                                </div>

                                <span className='material-icons-outlined text-scogoorange absolute left-2 top-2 font-normal text-font16 inputFontSize'>event</span>

                                {!readOnly && (value?.length > 0) && (
                                    <button
                                        type="button"
                                        className="absolute right-2 top-2 text-scogogray hover:text-scogoorange"
                                        onClick={(e) => {
                                            e.stopPropagation(); // prevent opening the picker
                                            setSelectedDates([]);
                                            onChange([]);
                                            if (onChangeHandler) onChangeHandler([]);
                                        }}
                                        title="Clear selection"
                                    >
                                        <span className="material-icons-outlined text-font16">close</span>
                                    </button>
                                )}


                                {isOpen && inputRef && !readOnly && (
                                    <div
                                        className="fixed z-[1001] shadow-lg"
                                        style={{
                                            top: `${inputRef.getBoundingClientRect().bottom + window.scrollY}px`,
                                            left: `${inputRef.getBoundingClientRect().left + window.scrollX}px`,
                                            position: 'fixed',
                                            zIndex: 999999999999999,
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <div className="bg-white rounded-lg shadow-lg p-4">
                                            <div className="flex gap-2">
                                                <ReactDatePicker
                                                    inline
                                                    selected={null}
                                                    onChange={handleDateToggle}
                                                    highlightDates={selectedDates}
                                                    selectsMultiple
                                                    dateFormat={dateFormat || "dd/MM/yyyy"}
                                                    maxDate={maxDate}
                                                    minDate={minDate}
                                                    disabled={readOnly}
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    scrollableYearDropdown
                                                    yearDropdownItemNumber={50}
                                                />

                                                <div className="mt-4">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Time</label>
                                                    <ReactDatePicker
                                                        selected={selectedTime}
                                                        onChange={(t) => handleTimeChange(t, onChange)}
                                                        showTimeSelect
                                                        showTimeSelectOnly
                                                        timeIntervals={30}
                                                        dateFormat="HH:mm"
                                                        className="w-full"
                                                        disabled={readOnly}
                                                        popperPlacement="top-start"
                                                        popperModifiers={[
                                                            {
                                                                name: 'preventOverflow',
                                                                options: {
                                                                    mainAxis: false,
                                                                },
                                                            },
                                                            {
                                                                name: 'offset',
                                                                options: {
                                                                    offset: [0, 10], // Adjust vertical offset if needed
                                                                },
                                                            },
                                                        ]}
                                                    />

                                                </div>
                                            </div>
                                            <div className="flex justify-end mt-4">
                                                <button
                                                    className="bg-scogoprimary text-white px-4 py-2 rounded"
                                                    onClick={() => handleDone(onChange)}
                                                >
                                                    Done
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {isOpen && !readOnly && (
                                    <div
                                        className="fixed inset-0 z-[1000]"
                                        onClick={() => setIsOpen(false)}
                                    />
                                )}
                            </>
                        );
                    }}
                />
                {_.get(errors, name) && <span className='text-scogoclosed text-font08 font-normal'>{_.get(errors, name).message}</span>}
            </div>
        </div>
    );
};

export default MultiSelectDateTimePicker;